import type {
  InfiniteData,
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { AnnotatedApiQueryMethod } from '@shared/utils/makeApiQuery';
import { API_QUERY_ANNOTATIONS } from '@shared/utils/makeApiQuery';

export const DEFAULT_PAGE_LIMIT = 20;

export const useApiInfiniteQuery = <
  TApi extends AnnotatedApiQueryMethod<any, any, any>,
  TResult extends any[] = Awaited<ReturnType<TApi>>,
  TError = Error,
>(
  api: TApi,
  args: any[],
  params?: Record<string, unknown> & { limit?: number },
  options?: Partial<
    UseInfiniteQueryOptions<
      TResult,
      TError,
      InfiniteData<TResult, number>,
      TResult,
      QueryKey,
      number
    >
  >,
): UseInfiniteQueryResult<InfiniteData<TResult, number>, TError> => {
  const { queryKey } =
    typeof api[API_QUERY_ANNOTATIONS] === 'function'
      ? api[API_QUERY_ANNOTATIONS](...args)
      : api[API_QUERY_ANNOTATIONS];

  const typedParams = params ?? {};
  const limit = typedParams.limit ?? DEFAULT_PAGE_LIMIT;

  return useInfiniteQuery({
    queryKey,
    initialPageParam: 0,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const pagination = { ...typedParams, offset: pageParam, limit };
      return (api as (...args: any[]) => Promise<TResult>)(...args, pagination);
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) =>
      Array.isArray(lastPage) && lastPage.length === limit ? lastPageParam + limit : undefined,
    ...options,
  } as UseInfiniteQueryOptions<TResult, TError, InfiniteData<TResult, number>, TResult, QueryKey, number>);
};
